<template>
  <div
    class="audio-embed"
    :style="{
      backgroundImage: `url(${thumbnailUrl})`
    }"
    ref="audio-embed"
  >
    <div class="p-absolute audio-embed__controls-overlay">
      <div
        class="audio-embed__secondary-control rewind-button"
        @click="player.rewind(10)"
      >
        <v-icon icon="replay_10"></v-icon>
      </div>

      <div class="mx-1 audio-action" data-cy="audio-play" @click="toggle">
        <play-button
          v-if="isLoading || isPaused"
          class="audio-embed__play"
          :is-loading="isLoading"
        />
        <v-icon v-else class="pause-button" icon="pause" size="2.5rem"></v-icon>
      </div>

      <div
        class="audio-embed__secondary-control forward-button"
        @click="player.forward(10)"
      >
        <v-icon icon="forward_10"></v-icon>
      </div>
    </div>
    <div
      @click="preventNavigation"
      data-cy="audio-embed"
      class="audio-embed__audio p-absolute"
    >
      <div v-if="isMounted" class="plyr__audio-embed">
        <audio controls crossorigin playsinline :id="playerId" />
      </div>
    </div>
  </div>
</template>

<script>
import Plyr from "plyr"
import Hls from "hls.js"

import { mapGetters } from "vuex"
import { isBuilderContext } from "@/lib/utils"
import isAndroid from "@/lib/is-platform/is-android"

import PlayButton from "@/components/PlayButton"

const ERRORS = {
  NOT_ALLOWED_ERROR: "NotAllowedError"
}

const wrapPlayer = (audioPlayer, vm) => ({
  play() {
    audioPlayer.play()
    vm.isPaused = false
    vm.isPlaying = true
  },

  pause() {
    audioPlayer.pause()
    vm.isPlaying = false
    vm.isPaused = true
  },

  rewind(seconds) {
    if (vm.isPlaying || vm.isPaused) {
      audioPlayer.rewind(seconds)
    }
  },

  forward(seconds) {
    if (vm.isPlaying || vm.isPaused) {
      audioPlayer.forward(seconds)
    }
  }
})

export default {
  components: {
    PlayButton
  },

  props: {
    audio: { type: Object, required: true },
    autoplay: { type: Boolean, default: false },
    loop: { type: Boolean, default: false },
    thumbnail: { type: String, default: null }
  },

  data() {
    return {
      isLoading: false,
      isMounted: false,
      isInitialized: false,
      isSeeking: false,
      isPlaying: false,
      isPaused: true,
      plyr: null,
      player: null
    }
  },

  computed: {
    ...mapGetters(["app"]),

    playerId() {
      return `audio-embed-${this._uid}`
    },

    thumbnailUrl() {
      return this.thumbnail
    }
  },

  mounted() {
    if (isBuilderContext()) return

    this.handleSetup()
  },

  beforeDestroy() {
    this.teardown()
  },

  activated() {
    this.handleSetup()
  },

  deactivated() {
    this.teardown()
  },

  methods: {
    initalizePlayer() {
      const vm = this

      return new Promise(resolve => {
        vm.isMounted = true
        vm.isLoading = true

        vm.$nextTick(() => {
          const audio = document.querySelector(`#${vm.playerId}`)

          vm.plyr = new Plyr(audio, {
            controls: ["progress", "current-time", "settings", "mute"],
            loop: { active: this.loop }
          })

          const configureHls = () => {
            const hls = new Hls()
            hls.loadSource(this.audio.embed_link)
            hls.attachMedia(audio)
          }

          if (isAndroid() && Hls.isSupported()) {
            configureHls()
          } else if (audio.canPlayType("application/vnd.apple.mpegurl")) {
            // Some browsers, like iOS Safari, support HLS Natively
            // https://docs.mux.com/guides/video/play-your-videos#3-use-the-hls-url-in-a-player
            audio.src = this.audio.embed_link
          } else if (Hls.isSupported()) {
            configureHls()
          } else {
            // TODO: Figure out fallback here?
            // Not sure when this would ever happen
          }

          vm.plyr.on("ready", () => {
            vm.isLoading = false

            resolve()
          })

          vm.plyr.on("ended", () => {
            this.isPlaying = false
            this.isPaused = true
          })

          vm.plyr.on("error", e => {
            if (e.detail.name === ERRORS.NOT_ALLOWED_ERROR) {
              this.isLoading = false
              this.isPaused = true
              this.isPlaying = false
            }
          })

          vm.player = wrapPlayer(vm.plyr, vm)
        })
      })
    },

    toggle(e) {
      this.preventNavigation(e)

      if (this.isLoading) return

      if (!isBuilderContext()) {
        if (this.isPaused || !this.isPlaying) {
          if (!this.isInitialized) {
            this.player.play()
          }
        } else {
          this.player.pause()
        }
      }
    },

    handleSetup() {
      this.isLoading = true
      this.initalizePlayer().then(() => {
        if (this.autoplay) {
          this.player.play()
        }
      })
    },

    preventNavigation(e) {
      e.stopPropagation()
      e.preventDefault()
    },

    teardown() {
      if (this.plyr) this.plyr.destroy()
      this.isPlaying = false
      this.isPaused = true
      this.isInitialized = false
    }
  }
}
</script>

<style lang="scss">
@import "~plyr/dist/plyr.css";
@import "@/assets/styles/variables";

$plyr-controls--height: 44px;

.audio-action {
  color: $color-ui--white;
  background: rgba($color-rgb--black, 0.56);
  width: $size--10;
  height: $size--10;
  border-radius: 50%;

  .pause-button {
    padding: 12px;
  }
}

.material-icons-round {
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.audio-embed__controls-overlay {
  height: calc(100% - #{$plyr-controls--height});
  display: flex;
  left: 50%;
  align-items: center;
  color: $color-ui--white;
  transform: translate(-50%, 0);
  height: auto;
  bottom: $plyr-controls--height;
  z-index: $video--controls-z-index;
}

.audio-embed__secondary-control {
  color: $color-ui--white;
  border-radius: 50px;
  height: $size--8;
  width: $size--8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  filter: drop-shadow(0 3px 8px rgba(60, 66, 87, 0.8));
}

.audio-embed__audio {
  width: 100%;
  bottom: 0;
  z-index: $video--controls-z-index;
}

.audio-embed {
  .plyr__control,
  .plyr__time {
    filter: drop-shadow(0 3px 8px rgba(60, 66, 87, 0.8));
  }

  .plyr__controls__item.plyr__volume {
    width: auto;
    min-width: 0;
  }

  .plyr__menu__container {
    background: $color-ui--white;
  }

  .plyr__menu__container
    .plyr__control[role="menuitemradio"][aria-checked="true"]:before {
    background: $color-ui--black;
  }
}

:root {
  --plyr-audio-controls-background: transparent;
  --plyr-audio-control-background-hover: transparent;
  --plyr-audio-control-color: #fff;
}
</style>
